import { ISnapConfig } from './../calendars.d';
import { differenceInMinutes, getHours, setHours, setMinutes, setSeconds } from 'date-fns';
import { ICalendarEvent } from '../calendars';

export enum WCS {
  cellHeight = 90,
  minuteHeight = cellHeight / 60,
  dayHeaderHeight = 80,
  menuWidth = 480,
  weekHeaderHeight = 70,
  timeLineWidth = 100,
  hoursPerDay = 24,
  minutesPerDay = hoursPerDay * 60,
  minutesPerHour = 60,
  gridPadding = 10,
  topNavOffset = 70,
  bottomNavOffset = 30,
  minSelectInterval = 15,
  menuWidthCalendarView = 480,
  menuBorderRadius = 16,
  stickyAreaHeight = 240,
  defaultHour = 6,
}

/**
 * Function to get current position of time of day in calendar
 */
export function getCurrentDayPosition({
  from,
  to,
  minHour,
}: {
  from: Date;
  to: Date;
  minHour: number;
}) {
  const minTime = setSeconds(setMinutes(setHours(from, minHour), 0), 0);
  if (differenceInMinutes(minTime, from) < 0) {
    return getEventPosition({ from, to, minHour });
  }
  return undefined;
}

/**
 * Function to calculate event position from rop based on dates and min calendar hour
 */
export function getEventPosition({ from, to, minHour }: { from: Date; to: Date; minHour: number }) {
  const minTime = setSeconds(setMinutes(setHours(from, minHour), 0), 0);
  const top = Math.abs(differenceInMinutes(minTime, from) * WCS.minuteHeight);
  const height = Math.abs(differenceInMinutes(from, to) * WCS.minuteHeight);

  return { top, height };
}

interface IGetHoursRange {
  [date: string]: { start: string; end: string }[];
}

/**
 * Get range of all hours in dataset
 * @param events events in calendar
 * @param markedDays marked days in calendar
 */
export function getHoursRange(
  events?: IGetHoursRange | { start: string; end: string }[],
  markedDays?: IGetHoursRange,
) {
  let min: number | undefined = undefined;
  let max: number | undefined = undefined;

  if (events) {
    const extracted = Array.isArray(events) ? events : Object.values(events).flat();

    for (const d of extracted) {
      const ds = getHours(new Date(d.start));
      const de = getHours(new Date(d.end));

      if (max === undefined || de > max) {
        max = de;
      }
      if (min === undefined || ds < min) {
        min = ds;
      }
    }
  }

  if (markedDays) {
    for (const key in markedDays) {
      if (Object.prototype.hasOwnProperty.call(markedDays, key)) {
        const element = markedDays[key];

        for (const d of element) {
          const ds = getHours(new Date(d.start));
          const de = getHours(new Date(d.end));

          if (max === undefined || de > max) {
            max = de;
          }

          if (min === undefined || ds < min) {
            min = ds;
          }
        }
      }
    }
  }

  return { min, max };
}

/**
 * Function to determine where to open menu
 */
export function whereToOpen(
  eventPosition?: DOMRectReadOnly,
  menuRect?: DOMRectReadOnly,
  menuWidth?: number,
) {
  if (!eventPosition) {
    return undefined;
  }

  let verticalPosition: 'down' | 'up';
  let horizontalPosition: 'left' | 'right';

  if (menuWidth && !menuRect) {
    verticalPosition = 'down';

    if (eventPosition.right + menuWidth <= window.innerWidth) {
      horizontalPosition = 'right';
    } else {
      horizontalPosition = 'left';
    }

    return {
      verticalPosition,
      horizontalPosition,
    };
  } else if (eventPosition && menuRect) {
    if (eventPosition.top + menuRect.height <= window.innerHeight) {
      verticalPosition = 'down';
    } else {
      verticalPosition = 'up';
    }

    if (eventPosition.right + (menuWidth || menuRect.width) <= window.innerWidth) {
      horizontalPosition = 'right';
    } else {
      horizontalPosition = 'left';
    }

    return {
      verticalPosition,
      horizontalPosition,
    };
  }

  return undefined;
}

/**
 * Function to create normal object from DOmRect
 * @param rect DomRect to copy
 * @returns
 */
export const getBoundingClientRectObj = (rect?: DOMRectReadOnly) => {
  if (!rect) {
    return undefined;
  }

  const { top, right, bottom, left, width, height, x, y } = rect;
  return { top, right, bottom, left, width, height, x, y };
};

function getContainerWidth(event: ICalendarEvent, computedEvents: any[]) {
  if (computedEvents.length === 0) {
    return 1;
  }

  for (let j = 0, m = computedEvents.length; j < m; j++) {
    const r = computedEvents[j];
    if (event.start < r.end) {
      return r.left;
    }
  }

  return 1;
}

export type TEventPosition = {
  top: number;
  left: number;
  width: number;
  height: number;
  zIndex: number;
  maxZIndex: number;
};

export type TPositionedEvent<T> = {
  position: TEventPosition;
  event: T;
};

/**
 * Function to calculate event position in calendar.
 * It calculate overlaps too!
 * Inspired by https://github.com/oliger/react-week-calendar/blob/master/src/Calendar/Day.js
 */
export function calculateEventPositions<T>(
  minHour: number,
  calendarEvents?: (T & { start: string; end: string })[],
  addedTopOffset: number = 0,
): TPositionedEvent<T>[] {
  if (!calendarEvents) {
    return [];
  }

  const events = [...calendarEvents].sort((a, b) =>
    a.start < b.start ? -1 : a.start > b.start ? 1 : 0,
  );

  const computedEvents: TPositionedEvent<T>[] = [];

  while (events.length > 0) {
    const e = events.shift();

    // Find overlapping events.
    const overlappingEvents = [e];
    while (events.length > 0 && e!.start <= events[0].start && e!.end > events[0].start) {
      overlappingEvents.push(events.shift());
    }

    // The container's width is the maximun space that we can use to render the
    // overlapping events.
    const containerWidth = getContainerWidth(e!, computedEvents);
    const width = containerWidth / overlappingEvents.length;

    computedEvents.push(
      ...overlappingEvents.map((oe, i) => {
        const pos = getEventPosition({
          from: new Date(oe?.start!),
          to: new Date(oe?.end!),
          minHour,
        });

        return {
          position: {
            top: pos.top + addedTopOffset,
            left: width * i,
            zIndex: i,
            width: 1 - i * width,
            height: pos.height,
            maxZIndex: overlappingEvents.length,
          },
          event: oe!,
        };
      }),
    );
  }

  return computedEvents;
}

/**
 * Function to determine if in viewpoint by %
 * @param rect DomRect
 * @param percentVisible how match if visible
 */
export function isElementXPercentInViewport(rect: DOMRect, percentVisible: number) {
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;

  return !(
    Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100) < percentVisible ||
    Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < percentVisible
  );
}

/**
 * Function to calculate menu offset based on position of event and menu.
 * @param param0
 * @returns
 */
export function calculateMenuAbsolutePosition({
  eventDims,
  menuDims,
  position,
  snapPoints,
  menuWidth,
  wrapperRect,
}: {
  eventDims?: DOMRect | undefined;
  menuDims?: DOMRect | undefined;
  position?: ReturnType<typeof whereToOpen>;
  wrapperRect?: DOMRect;
  snapPoints: ISnapConfig;
  menuWidth: number;
}) {
  let top: number | undefined = undefined;
  let left: number | undefined = undefined;
  const topSnap = wrapperRect
    ? wrapperRect.top + WCS.dayHeaderHeight + WCS.weekHeaderHeight
    : snapPoints.top;
  const bottomSnap = wrapperRect ? 0 : snapPoints.bottom;
  const parentHeight = wrapperRect ? wrapperRect.bottom : window.innerHeight;

  if (!position || !menuDims || !eventDims) {
    return { top, left };
  }

  if (position.verticalPosition === 'up') {
    if (bottomSnap) {
      top = parentHeight - menuDims.height - bottomSnap;
    } else
      top =
        parentHeight - eventDims.top - WCS.bottomNavOffset > 0
          ? parentHeight - menuDims.height - WCS.bottomNavOffset
          : eventDims.top - menuDims.height;
  } else {
    if (topSnap) {
      top = Math.max(eventDims.top, topSnap);
    } else {
      top =
        eventDims.bottom - WCS.topNavOffset > 0
          ? Math.max(eventDims.top - WCS.menuBorderRadius, WCS.topNavOffset)
          : eventDims.bottom;
    }
  }

  if (position.horizontalPosition === 'right') {
    left = eventDims.right;
  } else {
    left = eventDims.left - menuWidth;
  }

  return { top, left };
}
